import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  MessageSquare,
  Plus,
  Send,
  Loader2,
  Users,
  User,
  Bot,
  CheckCircle,
  ChevronRight,
  Search,
  ArrowLeft,
  Settings,
  X,
  UserPlus,
  Archive,
  RefreshCw,
  Zap,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { formatTime } from '../lib/utils';

const TACO_MASCOT_URL = "https://customer-assets.emergentagent.com/job_ttouchmanage/artifacts/sua7mu0g_Untitled%20design%20%288%29.png";

const TacoChatsPage = () => {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const { api, user, isAdmin } = useAuth();
  
  // Feature flag check
  const [chatEnabled, setChatEnabled] = useState(null);
  
  // Conversation list
  const [conversations, setConversations] = useState([]);
  const [loadingConversations, setLoadingConversations] = useState(true);
  
  // Active conversation
  const [activeConversation, setActiveConversation] = useState(null);
  const [loadingMessages, setLoadingMessages] = useState(false);
  
  // Message input
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  
  // Create conversation modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newConvTitle, setNewConvTitle] = useState('');
  const [newConvType, setNewConvType] = useState('group');
  const [selectedParticipants, setSelectedParticipants] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [creating, setCreating] = useState(false);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  
  // Check if chat is enabled
  useEffect(() => {
    checkChatEnabled();
  }, []);
  
  useEffect(() => {
    if (chatEnabled) {
      loadConversations();
    }
  }, [chatEnabled]);
  
  useEffect(() => {
    if (conversationId && chatEnabled) {
      loadConversation(conversationId);
    } else {
      setActiveConversation(null);
    }
  }, [conversationId, chatEnabled]);
  
  const checkChatEnabled = async () => {
    try {
      // Try to list conversations - if disabled, it will return 503
      const res = await api().get('/taco/v2/conversations');
      setChatEnabled(true);
    } catch (error) {
      if (error.response?.status === 503) {
        setChatEnabled(false);
      } else {
        setChatEnabled(true); // Assume enabled if other error
      }
    }
  };
  
  const loadConversations = async () => {
    setLoadingConversations(true);
    try {
      const res = await api().get('/taco/v2/conversations');
      setConversations(res.data.conversations || []);
    } catch (error) {
      console.error('Failed to load conversations:', error);
    } finally {
      setLoadingConversations(false);
    }
  };
  
  const loadConversation = async (id) => {
    setLoadingMessages(true);
    try {
      const res = await api().get(`/taco/v2/conversations/${id}`);
      setActiveConversation(res.data);
    } catch (error) {
      console.error('Failed to load conversation:', error);
      toast.error('Failed to load conversation');
      navigate('/chats');
    } finally {
      setLoadingMessages(false);
    }
  };
  
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);
  
  useEffect(() => {
    scrollToBottom();
  }, [activeConversation?.messages, scrollToBottom]);
  
  const sendMessage = async (e) => {
    e?.preventDefault();
    if (!newMessage.trim() || !activeConversation) return;
    
    setSending(true);
    const messageText = newMessage;
    setNewMessage('');
    
    // Optimistic update
    const tempMessage = {
      id: `temp_${Date.now()}`,
      sender_id: user?.id,
      sender_name: user?.full_name || 'You',
      sender_role: user?.role,
      message_type: 'user',
      content_text: messageText,
      created_at: new Date().toISOString(),
    };
    
    setActiveConversation(prev => ({
      ...prev,
      messages: [...(prev?.messages || []), tempMessage]
    }));
    
    try {
      const res = await api().post(`/taco/v2/conversations/${activeConversation.id}/messages`, {
        content: messageText,
        include_cross_context: false
      });
      
      // Reload to get the actual messages including Taco's response
      await loadConversation(activeConversation.id);
      
      if (res.data.taco_responded) {
        // Already handled by reload
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Failed to send message');
      // Rollback optimistic update
      setActiveConversation(prev => ({
        ...prev,
        messages: prev?.messages?.filter(m => m.id !== tempMessage.id)
      }));
      setNewMessage(messageText);
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };
  
  const askTaco = async () => {
    if (!newMessage.trim() || !activeConversation) return;
    
    setSending(true);
    const messageText = newMessage;
    setNewMessage('');
    
    try {
      await api().post(`/taco/v2/conversations/${activeConversation.id}/taco/respond`, {
        content: messageText,
        include_cross_context: true
      });
      
      await loadConversation(activeConversation.id);
      toast.success('Taco responded');
    } catch (error) {
      console.error('Failed to ask Taco:', error);
      toast.error('Failed to get Taco response');
      setNewMessage(messageText);
    } finally {
      setSending(false);
    }
  };
  
  // Create conversation handlers
  const loadAvailableUsers = async () => {
    try {
      const res = await api().get('/taco/v2/conversations/users/available', {
        params: { search: userSearch || undefined }
      });
      setAvailableUsers(res.data.users || []);
    } catch (error) {
      console.error('Failed to load users:', error);
    }
  };
  
  useEffect(() => {
    if (showCreateModal) {
      loadAvailableUsers();
    }
  }, [showCreateModal, userSearch]);
  
  const createConversation = async () => {
    if (!newConvTitle.trim()) {
      toast.error('Please enter a title');
      return;
    }
    
    if (newConvType === 'private' && selectedParticipants.length !== 1) {
      toast.error('Select exactly one participant for private chat');
      return;
    }
    
    if (newConvType === 'group' && selectedParticipants.length === 0) {
      toast.error('Select at least one participant');
      return;
    }
    
    setCreating(true);
    try {
      const res = await api().post('/taco/v2/conversations', {
        title: newConvTitle,
        type: newConvType,
        participant_ids: selectedParticipants.map(u => u.id)
      });
      
      toast.success(res.data.existing ? 'Opened existing conversation' : 'Conversation created');
      setShowCreateModal(false);
      setNewConvTitle('');
      setSelectedParticipants([]);
      
      await loadConversations();
      navigate(`/chats/${res.data.conversation.id}`);
    } catch (error) {
      console.error('Failed to create conversation:', error);
      toast.error(error.response?.data?.detail || 'Failed to create conversation');
    } finally {
      setCreating(false);
    }
  };
  
  // Not enabled state
  if (chatEnabled === false) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <MessageSquare className="h-12 w-12 text-zinc-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-zinc-900">Taco Chats Not Enabled</h3>
          <p className="text-zinc-500 mt-2">Contact your administrator to enable this feature.</p>
        </div>
      </div>
    );
  }
  
  // Loading state
  if (chatEnabled === null) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
      </div>
    );
  }
  
  return (
    <div className="h-[calc(100vh-140px)] flex">
      {/* Conversation List Sidebar */}
      <div className={cn(
        "w-80 border-r border-zinc-200 flex flex-col bg-zinc-50",
        activeConversation && "hidden md:flex"
      )}>
        {/* Header */}
        <div className="p-4 border-b border-zinc-200 bg-white">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-amber-500" />
              Chats
            </h2>
            <Button
              size="sm"
              onClick={() => setShowCreateModal(true)}
              className="bg-amber-500 hover:bg-amber-600"
              data-testid="new-chat-btn"
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={loadConversations}
              disabled={loadingConversations}
            >
              <RefreshCw className={cn("h-4 w-4", loadingConversations && "animate-spin")} />
            </Button>
          </div>
        </div>
        
        {/* Conversation List */}
        <ScrollArea className="flex-1">
          {loadingConversations ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-zinc-400" />
            </div>
          ) : conversations.length === 0 ? (
            <div className="text-center py-8 px-4">
              <MessageSquare className="h-10 w-10 text-zinc-300 mx-auto mb-3" />
              <p className="text-zinc-500 text-sm">No conversations yet</p>
              <Button
                variant="outline"
                size="sm"
                className="mt-3"
                onClick={() => setShowCreateModal(true)}
              >
                Start a chat
              </Button>
            </div>
          ) : (
            <div className="p-2 space-y-1">
              {conversations.map((conv) => (
                <button
                  key={conv.id}
                  onClick={() => navigate(`/chats/${conv.id}`)}
                  className={cn(
                    "w-full text-left p-3 rounded-lg transition-colors",
                    conv.id === conversationId
                      ? "bg-amber-100 border border-amber-200"
                      : "hover:bg-zinc-100"
                  )}
                  data-testid={`conversation-${conv.id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={cn(
                      "w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0",
                      conv.type === 'group' ? "bg-blue-100" : "bg-zinc-100"
                    )}>
                      {conv.type === 'group' ? (
                        <Users className="h-4 w-4 text-blue-600" />
                      ) : (
                        <User className="h-4 w-4 text-zinc-600" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-zinc-900 truncate">
                        {conv.title}
                      </p>
                      {conv.last_message_preview && (
                        <p className="text-xs text-zinc-500 truncate mt-0.5">
                          {conv.last_message_preview.is_taco && (
                            <span className="text-amber-600">Taco: </span>
                          )}
                          {conv.last_message_preview.text}
                        </p>
                      )}
                    </div>
                    {conv.unread_count > 0 && (
                      <Badge className="bg-amber-500">{conv.unread_count}</Badge>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </ScrollArea>
      </div>
      
      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col bg-white">
        {activeConversation ? (
          <>
            {/* Chat Header */}
            <div className="p-4 border-b border-zinc-200 flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                className="md:hidden"
                onClick={() => navigate('/chats')}
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div className={cn(
                "w-10 h-10 rounded-full flex items-center justify-center",
                activeConversation.type === 'group' ? "bg-blue-100" : "bg-zinc-100"
              )}>
                {activeConversation.type === 'group' ? (
                  <Users className="h-5 w-5 text-blue-600" />
                ) : (
                  <User className="h-5 w-5 text-zinc-600" />
                )}
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-zinc-900">{activeConversation.title}</h3>
                <p className="text-xs text-zinc-500">
                  {activeConversation.participant_details?.length || 0} participants
                </p>
              </div>
              <img 
                src={TACO_MASCOT_URL} 
                alt="Taco" 
                className="h-8 w-8 rounded-full object-cover border-2 border-amber-200"
                title="Taco is listening..."
              />
            </div>
            
            {/* Messages */}
            <ScrollArea className="flex-1 p-4">
              {loadingMessages ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-zinc-400" />
                </div>
              ) : (
                <div className="space-y-4">
                  {activeConversation.messages?.map((msg) => (
                    <div
                      key={msg.id}
                      className={cn(
                        "flex gap-3",
                        msg.sender_id === user?.id ? "flex-row-reverse" : ""
                      )}
                    >
                      {/* Avatar */}
                      <div className="flex-shrink-0">
                        {msg.message_type === 'assistant' ? (
                          <img 
                            src={TACO_MASCOT_URL} 
                            alt="Taco" 
                            className="h-8 w-8 rounded-full object-cover border border-amber-200"
                          />
                        ) : msg.message_type === 'system' ? (
                          <div className="h-8 w-8 rounded-full bg-zinc-100 flex items-center justify-center">
                            <Settings className="h-4 w-4 text-zinc-400" />
                          </div>
                        ) : (
                          <div className="h-8 w-8 rounded-full bg-zinc-200 flex items-center justify-center">
                            <span className="text-xs font-medium text-zinc-600">
                              {msg.sender_name?.charAt(0)?.toUpperCase() || '?'}
                            </span>
                          </div>
                        )}
                      </div>
                      
                      {/* Message Content */}
                      <div className={cn(
                        "max-w-[70%] rounded-2xl px-4 py-2",
                        msg.message_type === 'system' 
                          ? "bg-zinc-100 text-zinc-600 text-center text-sm italic"
                          : msg.message_type === 'assistant'
                            ? "bg-amber-50 border border-amber-100 rounded-bl-md"
                            : msg.sender_id === user?.id
                              ? "bg-zinc-900 text-white rounded-br-md"
                              : "bg-zinc-100 rounded-bl-md"
                      )}>
                        {msg.message_type !== 'system' && msg.sender_id !== user?.id && (
                          <p className="text-xs font-medium text-zinc-500 mb-1">
                            {msg.sender_name}
                            {msg.sender_role && ` (${msg.sender_role})`}
                          </p>
                        )}
                        <p className="whitespace-pre-wrap">{msg.content_text}</p>
                        
                        {/* Tool calls */}
                        {msg.tool_calls?.length > 0 && (
                          <div className="mt-2 pt-2 border-t border-amber-200/50">
                            <p className="text-xs text-amber-700 mb-1">Actions taken:</p>
                            <div className="flex flex-wrap gap-1">
                              {msg.tool_calls.map((tc, idx) => (
                                <Badge 
                                  key={idx}
                                  variant="outline"
                                  className="text-xs bg-green-50 text-green-700 border-green-200"
                                >
                                  <CheckCircle className="h-3 w-3 mr-1" />
                                  {tc.name || tc}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        <p className={cn(
                          "text-xs mt-1",
                          msg.sender_id === user?.id ? "text-white/60" : "text-zinc-400"
                        )}>
                          {formatTime(msg.created_at)}
                        </p>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </ScrollArea>
            
            {/* Message Input */}
            <div className="p-4 border-t border-zinc-200">
              <form onSubmit={sendMessage} className="flex gap-2">
                <Input
                  ref={inputRef}
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Type a message... (use @taco to ask Taco)"
                  disabled={sending}
                  className="flex-1"
                  data-testid="chat-message-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={askTaco}
                  disabled={!newMessage.trim() || sending}
                  title="Ask Taco directly"
                  data-testid="ask-taco-btn"
                >
                  {sending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <Zap className="h-4 w-4 mr-1 text-amber-500" />
                      Ask Taco
                    </>
                  )}
                </Button>
                <Button
                  type="submit"
                  disabled={!newMessage.trim() || sending}
                  className="bg-amber-500 hover:bg-amber-600"
                  data-testid="send-message-btn"
                >
                  {sending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                </Button>
              </form>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <img 
                src={TACO_MASCOT_URL} 
                alt="Taco" 
                className="h-24 w-24 rounded-full object-cover border-4 border-amber-100 mx-auto mb-4"
              />
              <h3 className="text-lg font-medium text-zinc-900">Select a conversation</h3>
              <p className="text-zinc-500 mt-2">Or start a new chat with your team</p>
              <Button
                className="mt-4 bg-amber-500 hover:bg-amber-600"
                onClick={() => setShowCreateModal(true)}
              >
                <Plus className="h-4 w-4 mr-2" />
                New Chat
              </Button>
            </div>
          </div>
        )}
      </div>
      
      {/* Create Conversation Modal */}
      {showCreateModal && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowCreateModal(false)}
        >
          <div 
            className="bg-white rounded-xl shadow-xl max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b border-zinc-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold">New Conversation</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowCreateModal(false)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
            
            <div className="p-4 space-y-4">
              <div>
                <label className="text-sm font-medium text-zinc-700">Title</label>
                <Input
                  value={newConvTitle}
                  onChange={(e) => setNewConvTitle(e.target.value)}
                  placeholder="Conversation title..."
                  className="mt-1"
                  data-testid="new-conv-title"
                />
              </div>
              
              <div>
                <label className="text-sm font-medium text-zinc-700">Type</label>
                <div className="flex gap-2 mt-1">
                  <Button
                    variant={newConvType === 'group' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setNewConvType('group')}
                    className={newConvType === 'group' ? 'bg-amber-500' : ''}
                  >
                    <Users className="h-4 w-4 mr-1" />
                    Group
                  </Button>
                  <Button
                    variant={newConvType === 'private' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setNewConvType('private')}
                    className={newConvType === 'private' ? 'bg-amber-500' : ''}
                  >
                    <User className="h-4 w-4 mr-1" />
                    Private
                  </Button>
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium text-zinc-700">Participants</label>
                <Input
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  placeholder="Search users..."
                  className="mt-1"
                />
                
                {/* Selected participants */}
                {selectedParticipants.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {selectedParticipants.map((u) => (
                      <Badge key={u.id} className="bg-amber-100 text-amber-800">
                        {u.full_name}
                        <button
                          className="ml-1 hover:text-amber-900"
                          onClick={() => setSelectedParticipants(prev => prev.filter(p => p.id !== u.id))}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
                
                {/* Available users */}
                <div className="mt-2 max-h-40 overflow-y-auto border rounded-lg">
                  {availableUsers.filter(u => !selectedParticipants.find(p => p.id === u.id)).map((u) => (
                    <button
                      key={u.id}
                      className="w-full text-left p-2 hover:bg-zinc-50 flex items-center gap-2"
                      onClick={() => {
                        if (newConvType === 'private') {
                          setSelectedParticipants([u]);
                        } else {
                          setSelectedParticipants(prev => [...prev, u]);
                        }
                      }}
                    >
                      <div className="h-6 w-6 rounded-full bg-zinc-200 flex items-center justify-center">
                        <span className="text-xs">{u.full_name?.charAt(0)}</span>
                      </div>
                      <div>
                        <p className="text-sm font-medium">{u.full_name}</p>
                        <p className="text-xs text-zinc-500">{u.role}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="p-4 border-t border-zinc-200 flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowCreateModal(false)}>
                Cancel
              </Button>
              <Button
                onClick={createConversation}
                disabled={creating}
                className="bg-amber-500 hover:bg-amber-600"
                data-testid="create-conv-btn"
              >
                {creating ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Plus className="h-4 w-4 mr-2" />
                )}
                Create
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TacoChatsPage;
